import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingSpinner, CustomizableSelect, IconClose } from "@velaro/velaro-component-lib";
import useAIConfigs from "@/hooks/useAIConfigs";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useSubscriptions from "@/hooks/useSubscriptions";
import { AIConfiguration } from "../types";
import AINewSettingsTabs from "./AINewSettingsTabs";

export default function AINewSettingsGeneral() {
  const { id } = useParams();
  const { aiConfigurations, loading, update } = useAIConfigs();
  const { azureIndexes } = useAzureIndexes();
  const { subscription } = useSubscriptions();
  const [config, setConfig] = useState<AIConfiguration>();
  const [configIndex, setConfigIndex] = useState<number>();
  const [selectedIndexName, setSelectedIndexName] = useState<string>("");

  useEffect(() => {
    if (aiConfigurations.length > 0 && id) {
      const index = aiConfigurations.findIndex((c) => c.id === parseInt(id));
      if (index !== -1) {
        setConfig(aiConfigurations[index]);
        setConfigIndex(index);
        setSelectedIndexName(aiConfigurations[index].indexName);
      }
    }
  }, [aiConfigurations, id]);

  const handleUpdate = (field: keyof AIConfiguration, value: any) => {
    if (!config || configIndex === undefined) return;
    const updated = { ...config, [field]: value };
    setConfig(updated);
    update(updated, configIndex);
  };

  const handleAzureIndex = (indexName: string) => {
    handleUpdate("indexName", indexName);
    setSelectedIndexName(indexName);
  };

  const models = [
    { label: "OpenAI", sublabel: "GPT 4o Mini", techName: "velaro-gpt-4o-mini" },
    { label: "OpenAI", sublabel: "GPT 4 Turbo", techName: "velaro-gpt-4" },
    { label: "OpenAI", sublabel: "GPT 4o", techName: "velaro-gpt-4o" },
    { label: "OpenAI", sublabel: "GPT 5", techName: "velaro-gpt-5" },
    { label: "OpenAI", sublabel: "GPT 5 Mini", techName: "velaro-gpt-5-mini" },
    { label: "Anthropic", sublabel: "Claude 3 Haiku", techName: "claude-3-haiku-20240307" },
    { label: "Anthropic", sublabel: "Claude 3.5 Haiku", techName: "claude-3-5-haiku-20241022" },
    { label: "Anthropic", sublabel: "Claude 3.5 Sonnet", techName: "claude-3-5-sonnet-20240620" },
    { label: "Google", sublabel: "Gemini 2.0 Flash", techName: "gemini-2.0-flash" },
    { label: "Google", sublabel: "Gemini 1.5 Pro", techName: "gemini-1.5-pro" }
  ];

  const languages = [
    { label: "Auto Detect" },
    { label: "English" },
    { label: "Spanish" }
  ];

  const replyLengths = [
    { label: "Short", sublabel: "(25 Words)" },
    { label: "Medium", sublabel: "(50 Words)" },
    { label: "Long", sublabel: "(100 Words)" }
  ];

  if (loading || !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        {/* Header */}
        <div className="border-b pb-4 mb-4">
          <div className="font-semibold text-lg">Chatbot Settings</div>
          <div className="text-slate-500 text-sm">
            Configure your chatbot easily with customizable settings.
          </div>
        </div>

        {/* Internal Chatbot Name */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Internal Chatbot Name</div>
          <div className="text-slate-500 text-sm pb-2">
            A unique identifier for organizing and managing your chatbots within the app. Not visible to your customers or visitors.
          </div>
          <input
            type="text"
            value={config.name}
            onChange={(e) => handleUpdate("name", e.target.value)}
            placeholder="Enter a name"
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* Instructions */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Instructions</div>
          <div className="text-slate-500 text-sm pb-2">
            Define how the AI chatbot interacts with users. It sets the tone, language and the behavior of the AI.
          </div>
          <textarea
            value={config.prompt}
            onChange={(e) => handleUpdate("prompt", e.target.value)}
            placeholder="You are a helpful customer support agent. Your primary job is to provide accurate and helpful answers to questions from the user you are interacting with. You're skilled at handling multi-turn conversations and you're capable of asking clarifying questions if the user's query is unclear."
            rows={5}
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* Tone */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Tone</div>
          <div className="text-slate-500 text-sm pb-2">
            Set the communication style for your chatbot responses.
          </div>
          <input
            type="text"
            value={config.tone}
            onChange={(e) => handleUpdate("tone", e.target.value)}
            placeholder="Be polite and friendly. Don't use slang and emojis."
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* AI Model */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">AI Model</div>
          <div className="text-slate-500 text-sm pb-2">
            The AI model will be used to generate answers for your chatbot.
          </div>
          {(subscription?.aiModelTier ?? "basic") !== "basic" ? (
            <div className="text-sm">
              <CustomizableSelect
                options={models
                  .filter((m) => {
                    const tier = subscription?.aiModelTier ?? "basic";
                    if (tier === "enterprise") return true;
                    if (tier === "pro") return !["velaro-gpt-5", "velaro-gpt-5-mini", "gemini-1.5-pro", "claude-3-5-sonnet-20240620"].includes(m.techName);
                    return ["velaro-gpt-4o-mini"].includes(m.techName);
                  })
                  .map((m) => ({
                    label: m.label,
                    sublabel: m.sublabel,
                    action: () => handleUpdate("aiModel", m.techName)
                  }))}
                defaultOption={(() => {
                  const model = models.find((m) => m.techName === config.aiModel) || models[0];
                  return {
                    label: model.label,
                    sublabel: model.sublabel,
                    action: () => handleUpdate("aiModel", model.techName)
                  };
                })()}
              />
            </div>
          ) : (
            <div className="text-sm text-slate-gray-500">
              {config.aiModel || "Velaro Default (GPT-4o Mini)"}
              <span className="ml-2 text-xs text-slate-gray-400">— upgrade your plan to choose a different model</span>
            </div>
          )}
        </div>

        {/* Language & Reply Length */}
        <div className="py-3">
          <div className="flex">
            <div className="flex flex-col w-1/2 pr-3">
              <div className="font-semibold text-sm pb-1">Language</div>
              <div className="text-sm">
                <CustomizableSelect
                  options={languages.map((l) => ({
                    label: l.label,
                    action: () => handleUpdate("language", l.label)
                  }))}
                  defaultOption={(() => {
                    const lang = languages.find((l) => l.label === config.language) || languages[0];
                    return {
                      label: lang.label,
                      action: () => handleUpdate("language", lang.label)
                    };
                  })()}
                />
              </div>
            </div>
            <div className="flex flex-col w-1/2">
              <div className="font-semibold text-sm pb-1">Reply Length</div>
              <div className="text-sm">
                <CustomizableSelect
                  options={replyLengths.map((r) => ({
                    label: r.label,
                    sublabel: r.sublabel,
                    action: () => handleUpdate("replyLength", r.label)
                  }))}
                  defaultOption={(() => {
                    const replyLength = replyLengths.find((r) => r.label === config.replyLength) || replyLengths[0];
                    return {
                      label: replyLength.label,
                      sublabel: replyLength.sublabel,
                      action: () => handleUpdate("replyLength", replyLength.label)
                    };
                  })()}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Temperature */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Temperature</div>
          <div className="text-slate-500 text-sm pb-2">
            Gets or sets what sampling temperature to use, between 0 and 2. Values above 1 will make the output more random, while lower values like 0.2 will make it more focused and deterministic (default: 1).
          </div>
          <input
            type="number"
            min={0}
            max={2}
            step={0.1}
            value={config.temperature ?? ""}
            onChange={(e) => handleUpdate("temperature", e.target.valueAsNumber)}
            placeholder="1"
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* Index - Hidden for now
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Index</div>
          <div className="text-slate-500 text-sm pb-2">
            A Unique Azure Index required for using File Vector Search. Consult Velaro Customer Support to have this modified.
          </div>
          <input
            type="text"
            value={config.indexName}
            onChange={(e) => handleUpdate("indexName", e.target.value)}
            placeholder=""
            className="w-full p-2 border rounded-md text-sm text-slate-500"
          />
        </div>
        */}

        {/* Training Index */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Training Index</div>
          <div className="text-slate-500 text-sm pb-2">
            A Unique Azure Index required for using File Vector Search. Consult Velaro Customer Support to have this modified.
          </div>
          <div className="flex w-1/2 pr-3">
            <div className="h-[40px] text-sm flex flex-1">
              <CustomizableSelect
                options={azureIndexes.map((index) => ({
                  label: index.name,
                  action: () => handleAzureIndex(index.name)
                }))}
                defaultOption={{
                  label: selectedIndexName || "Select an index",
                  action: () => {}
                }}
              />
              {selectedIndexName && (
                <button className="pl-2" onClick={() => handleAzureIndex("")}>
                  <IconClose className="text-slate-gray-500" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
